import type { TFunction } from 'i18next';
import styled from '@emotion/styled';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { Text, tokens } from '@hotjar/hotsauce';
import { capitalize } from '@hotjar/insights-common/lib/string';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { useReadableProductName } from '@hotjar/webapp/domains/common/product/hooks/useReadableProductName';
import { PlanChangeListItem } from './PlanChangeListItem';
import type { ChangeListItem, Plan } from './types';

interface Props {
  changeList: ChangeListItem[];
  isFirstColumnNarrow?: boolean;
}

export const S = {
  Text: styled(Text)<{ isStrikeThroughText?: boolean }>`
    margin-right: ${tokens.spacer.x2};
    white-space: nowrap;
    ${(props) =>
      props.isStrikeThroughText &&
      `
        color: ${tokens.color.text_disabled};
        text-decoration: line-through;
      `}
  `,
};

// This will match the current plans with the new ones based on the sku
const preparePlans = (currentPlans: Plan[], newPlans: Plan[]) => {
  let relatedPlans: [Plan, Plan | undefined];
  return currentPlans.reduce<[Plan, Plan | undefined][]>((map, currentPlan) => {
    relatedPlans = [
      currentPlan,
      newPlans.find((newPlan) => newPlan.productType === currentPlan.productType),
    ];

    // We need the Observe SKU to be at first
    if (currentPlan.productType === ProductType.Analytics) {
      map.unshift(relatedPlans);
    } else {
      map.push(relatedPlans);
    }

    return map;
  }, []);
};

const getPlanLimitText = (tSummaryOfChanges: TFunction, plan: Plan) => {
  return plan.productType === ProductType.Analytics
    ? tSummaryOfChanges('plan-change-list.sessions', { limit: plan.limit })
    : tSummaryOfChanges('plan-change-list.answers', { limit: plan.limit });
};

export function PlanChangeList({ changeList, isFirstColumnNarrow }: Props): JSX.Element {
  const { t: tSummaryOfChanges } = useTranslationWithPrefix('billing:summary-of-changes');

  const productTypeByReadableProductName = {
    [ProductType.Analytics]: useReadableProductName(ProductType.Analytics),
    [ProductType.Feedback]: useReadableProductName(ProductType.Feedback),
  };

  return (
    <>
      {changeList.map((item) => (
        <PlanChangeListItem
          key={item.siteLink}
          siteLink={item.siteLink}
          detailedList={{
            isFirstColumnNarrow,
            headerItems: [
              capitalize(tSummaryOfChanges('plan-change-list.previous-plan')),
              capitalize(tSummaryOfChanges('plan-change-list.new-plan')),
            ],
            items: preparePlans(item.currentPlans, item.newPlans).map(([currentPlan, newPlan]) => ({
              title: capitalize(productTypeByReadableProductName[currentPlan.productType]),
              data: [
                <span key="current">
                  <S.Text textColor="text_disabled" isStrikeThroughText>
                    {capitalize(
                      tSummaryOfChanges('plan-change-list.type-plan', { type: currentPlan.type }),
                    )}
                  </S.Text>
                  <S.Text textType="caption" textColor="text_disabled" isStrikeThroughText>
                    {getPlanLimitText(tSummaryOfChanges, currentPlan)}
                  </S.Text>
                </span>,
                <span key="new">
                  <S.Text textType="body2">
                    {!newPlan
                      ? '--'
                      : capitalize(
                          tSummaryOfChanges('plan-change-list.type-plan', { type: newPlan?.type }),
                        )}
                  </S.Text>
                  <S.Text textType="caption" textColor="text_light">
                    {newPlan && getPlanLimitText(tSummaryOfChanges, newPlan)}
                  </S.Text>
                </span>,
              ],
            })),
          }}
        />
      ))}
    </>
  );
}
